import type React from "react";
import { twJoin } from "tailwind-merge";

interface PageGridRowProps {
  children: React.ReactNode[] | React.ReactNode;
}

function PageGridRow({ children }: PageGridRowProps): React.ReactNode {
  return <section className="grid w-full grid-cols-12 gap-space-md @lg:gap-space-lg">{children}</section>;
}

interface PageGridColumnProps {
  children: React.ReactNode[] | React.ReactNode;
  size: "25%" | "33%" | "50%" | "66%" | "75%" | "100%";
  isDesktopIndifferent?: boolean; // Apply the requested proportion from medium container widths.
  /** Keeps detail/sidebar layouts side by side on compact container widths. */
  sizeFrom?: "sm";
}

function PageGridColumn({ children, size, isDesktopIndifferent, sizeFrom }: PageGridColumnProps): React.ReactNode {
  return (
    <section
      className={twJoin(
        "flex w-full flex-col gap-space-md @lg:gap-space-lg",
        "col-span-full @md:col-span-6",
        // Hide appearence when no children are present
        "[&:not(:has(*))]:hidden",
        // 1 / 4
        size === "25%" && "@lg:col-span-3",
        size === "25%" && isDesktopIndifferent && "@md:col-span-3",
        size === "25%" && sizeFrom === "sm" && "@sm:col-span-3",
        // 1 / 3
        size === "33%" && "@lg:col-span-4",
        size === "33%" && isDesktopIndifferent && "@md:col-span-4",
        size === "33%" && sizeFrom === "sm" && "@sm:col-span-4",
        // 1 / 2
        // Exception: When a 50% size item is the only child, it should take 66% instead
        size === "50%" && "@lg:col-span-6 @lg:first:last:col-span-8",
        // 2 / 3
        size === "66%" && "@lg:col-span-8",
        size === "66%" && isDesktopIndifferent && "@md:col-span-8",
        size === "66%" && sizeFrom === "sm" && "@sm:col-span-8",
        // 3 / 4
        size === "75%" && "@lg:col-span-9",
        size === "75%" && isDesktopIndifferent && "@md:col-span-9",
        size === "75%" && sizeFrom === "sm" && "@sm:col-span-9",
        // 1 / 1
        size === "100%" && "@md:col-span-full",
      )}
    >
      {children}
    </section>
  );
}

export const PageGrid = {
  Row: PageGridRow,
  Column: PageGridColumn,
};
